import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, Users, FileText, Calendar as CalendarIcon, Download } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

const Payroll = () => {
  const { token } = useAuth();
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    employee_name: "",
    employee_id: "",
    period_start: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"),
    period_end: format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), "yyyy-MM-dd"),
    gross_salary: "",
    tax_rate: 30,
    employer_contribution_rate: 31.42,
    vacation_pay_rate: 12
  });

  useEffect(() => {
    fetchPayrolls();
  }, []);

  const fetchPayrolls = async () => {
    try {
      const response = await axios.get(`${API}/payroll`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayrolls(response.data);
    } catch (error) {
      console.error("Error fetching payrolls:", error);
      toast.error("Kunde inte hämta löner");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payroll`, {
        ...formData,
        gross_salary: parseFloat(formData.gross_salary)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lön skapad");
      fetchPayrolls();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa lön");
    }
  };

  const handleStatusChange = async (payrollId, status) => {
    try {
      await axios.put(`${API}/payroll/${payrollId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status uppdaterad");
      fetchPayrolls();
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const handleDownloadPayslip = async (payrollId, employeeName) => {
    try {
      // Download PDF
      const response = await axios.get(`${API}/payroll/${payrollId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `lonebesked_${employeeName.replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Lönebesked nedladdad som PDF");
    } catch (error) {
      toast.error("Kunde inte ladda ner lönebesked");
    }
  };

  const handleExportAGI = async () => {
    const currentYear = new Date().getFullYear();
    const periodStart = `${currentYear}-01-01`;
    const periodEnd = `${currentYear}-12-31`;
    
    try {
      const response = await axios.get(`${API}/payroll/export/agi`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period_start: periodStart, period_end: periodEnd }
      });
      
      // Convert to JSON file download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AGI_${currentYear}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("AGI-rapport exporterad");
    } catch (error) {
      toast.error("Kunde inte exportera AGI-rapport");
    }
  };

  const handleExportSRU = async () => {
    const currentYear = new Date().getFullYear();
    
    try {
      const response = await axios.get(`${API}/payroll/export/sru`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: currentYear },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AGI_${currentYear}.sru`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("SRU-fil exporterad för Skatteverket");
    } catch (error) {
      toast.error("Kunde inte exportera SRU-fil");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_name: "",
      employee_id: "",
      period_start: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"),
      period_end: format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), "yyyy-MM-dd"),
      gross_salary: "",
      tax_rate: 30,
      employer_contribution_rate: 31.42,
      vacation_pay_rate: 12
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="badge-success">Godkänd</Badge>;
      case "paid":
        return <Badge className="badge-success">Utbetald</Badge>;
      default:
        return <Badge className="badge-pending">Utkast</Badge>;
    }
  };

  // Calculate preview
  const calculatePreview = () => {
    const gross = parseFloat(formData.gross_salary) || 0;
    const tax = gross * (formData.tax_rate / 100);
    const net = gross - tax;
    const employer = gross * (formData.employer_contribution_rate / 100);
    const vacation = gross * (formData.vacation_pay_rate / 100);
    const total = gross + employer + vacation;
    return { gross, tax, net, employer, vacation, total };
  };

  const preview = calculatePreview();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="payroll">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Lönehantering</h1>
          <p className="text-slate-500 mt-1">Skapa och hantera löner</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExportAGI} data-testid="export-agi-btn">
            <Download size={16} className="mr-2" />
            AGI-rapport
          </Button>
          <Button variant="outline" onClick={handleExportSRU} data-testid="export-sru-btn">
            <FileText size={16} className="mr-2" />
            SRU-fil
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-payroll-btn">
                <Plus size={16} className="mr-2" />
                Ny lön
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Skapa lön</DialogTitle>
              <DialogDescription>Fyll i uppgifter för löneutbetalning</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_name">Anställd namn</Label>
                  <Input
                    id="employee_name"
                    value={formData.employee_name}
                    onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                    placeholder="Anna Andersson"
                    required
                    data-testid="payroll-employee-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Anställningsnummer</Label>
                  <Input
                    id="employee_id"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="EMP001"
                    data-testid="payroll-employee-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Periodstart</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="period-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.period_start}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={new Date(formData.period_start)}
                        onSelect={(date) => date && setFormData({ ...formData, period_start: format(date, "yyyy-MM-dd") })}
                        locale={sv}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Periodslut</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="period-end">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.period_end}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={new Date(formData.period_end)}
                        onSelect={(date) => date && setFormData({ ...formData, period_end: format(date, "yyyy-MM-dd") })}
                        locale={sv}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gross_salary">Bruttolön</Label>
                  <Input
                    id="gross_salary"
                    type="number"
                    value={formData.gross_salary}
                    onChange={(e) => setFormData({ ...formData, gross_salary: e.target.value })}
                    placeholder="35000"
                    required
                    data-testid="payroll-gross"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax_rate">Skattesats (%)</Label>
                  <Input
                    id="tax_rate"
                    type="number"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })}
                    step="0.1"
                    data-testid="payroll-tax-rate"
                  />
                </div>
              </div>

              {/* Preview */}
              {formData.gross_salary && (
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3">Förhandsvisning</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Bruttolön:</span>
                        <span className="font-medium">{formatCurrency(preview.gross)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Skatt ({formData.tax_rate}%):</span>
                        <span className="font-medium text-red-600">-{formatCurrency(preview.tax)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Nettolön:</span>
                        <span className="font-bold text-green-600">{formatCurrency(preview.net)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Arb.avg ({formData.employer_contribution_rate}%):</span>
                        <span className="font-medium">{formatCurrency(preview.employer)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Semesterersättning ({formData.vacation_pay_rate}%):</span>
                        <span className="font-medium">{formatCurrency(preview.vacation)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-medium">Total kostnad:</span>
                        <span className="font-bold">{formatCurrency(preview.total)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Avbryt
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-payroll-btn">
                  Skapa lön
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-base p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Users className="text-indigo-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{payrolls.length}</p>
              <p className="text-sm text-slate-500">Totalt antal</p>
            </div>
          </div>
        </Card>
        <Card className="card-base p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <FileText className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {payrolls.filter(p => p.status === "draft").length}
              </p>
              <p className="text-sm text-slate-500">Utkast</p>
            </div>
          </div>
        </Card>
        <Card className="card-base p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <FileText className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(payrolls.reduce((sum, p) => sum + (p.total_cost || 0), 0))}
              </p>
              <p className="text-sm text-slate-500">Total kostnad</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Payrolls List */}
      <Card className="card-base overflow-hidden" data-testid="payroll-list">
        <CardContent className="p-0">
          {payrolls.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Anställd</th>
                  <th>Period</th>
                  <th className="text-right">Brutto</th>
                  <th className="text-right">Netto</th>
                  <th className="text-right">Total kostnad</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {payrolls.map((payroll) => (
                  <tr key={payroll.id} data-testid={`payroll-row-${payroll.id}`}>
                    <td>
                      <div>
                        <p className="font-medium">{payroll.employee_name}</p>
                        {payroll.employee_id && (
                          <p className="text-xs text-slate-500 font-mono">{payroll.employee_id}</p>
                        )}
                      </div>
                    </td>
                    <td className="text-sm">
                      {payroll.period_start} - {payroll.period_end}
                    </td>
                    <td className="amount-cell">{formatCurrency(payroll.gross_salary)}</td>
                    <td className="amount-cell text-green-600 font-medium">
                      {formatCurrency(payroll.net_salary)}
                    </td>
                    <td className="amount-cell font-medium">{formatCurrency(payroll.total_cost)}</td>
                    <td className="text-center">{getStatusBadge(payroll.status)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPayslip(payroll.id, payroll.employee_name)}
                          data-testid={`download-payslip-${payroll.id}`}
                        >
                          <Download size={14} className="mr-1" />
                          Lönespec
                        </Button>
                        {payroll.status === "draft" && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleStatusChange(payroll.id, "approved")}
                            data-testid={`approve-payroll-${payroll.id}`}
                          >
                            Godkänn
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <Users className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga löner skapade ännu</p>
              <p className="text-sm mt-1">Klicka på "Ny lön" för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Payroll;
